import {
  Component,
  EventEmitter,
  Input,
  Output,
  HostListener,
} from '@angular/core';

// Filter interfaces
export interface FilterState {
  // Basic Information
  religion: string[];
  ageMin: number | null;
  ageMax: number | null;
  nationality: string[];
  maritalStatus: string[];
  gender: string;

  // Personality & Lifestyle
  interest: string[];
  values: string[];
  alcoholConsumption: string[];
  smoker: string[];
  children: string[];
  iUsuallyWakeUpAt: string[];
  hobbies: string[];
  fitness: string[];
  personality: string[];
  lifeGoals: string[];
  maxionGoals: string[];
  meetVibes: string[];

  // Signup & Profile Status
  profileStepNo: { min: number | null; max: number | null };
  signupStepNo: { min: number | null; max: number | null };
  passportVerified: string;
  subscriptionStatus: string[];

  // Activity-Based Filters
  noMatchesInLastDays: number;
  numberOfLikes?: number | null;
}

export interface FilterChip {
  label: string;
  value: string[] | string;
  key: string;
}

@Component({
  selector: 'app-filter',
  templateUrl: './filter.component.html',
  styleUrls: ['./filter.component.css'],
})
export class FilterComponent {
  @Input() showFilters: boolean = false;
  @Input() activeFilterSection: string = '';
  @Input() selectedFilters: FilterState = {
    religion: [],
    ageMin: null,
    ageMax: null,
    nationality: [],
    maritalStatus: [],
    gender: '',
    interest: [],
    values: [],
    alcoholConsumption: [],
    smoker: [],
    children: [],
    iUsuallyWakeUpAt: [],
    profileStepNo: { min: null, max: null },
    signupStepNo: { min: null, max: null },
    passportVerified: '',
    subscriptionStatus: [],
    noMatchesInLastDays: 0,
    numberOfLikes: null,
    hobbies: [],
    fitness: [],
    personality: [],
    lifeGoals: [],
    maxionGoals: [],
    meetVibes: [],
  };
  @Input() activeFilterChips: FilterChip[] = [];
  @Input() showNumberOfLikes: boolean = false; // To control if numberOfLikes field is shown

  // Dropdown state management
  openDropdowns: { [key: string]: boolean } = {};

  // Filter options - these can be overridden by parent components
  @Input() religionOptions: string[] = [
    'Agnostic',
    'Atheist',
    'Buddhist',
    'Catholic',
    'Christian',
    'Hindu',
    'Jewish',
    'Muslim',
    'Sikh',
    'Spiritual',
    'Prefer not to say',
    'Other',
  ];
  @Input() nationalityOptions: any[] = [];
  @Input() maritalStatusOptions: string[] = [
    'Never Married',
    'Married',
    'Divorced',
    'Separated',
  ];
  @Input() genderOptions: string[] = ['Male', 'Female'];
  @Input() interestOptions: any[] = [];
  @Input() valueOptions: any[] = [];
  @Input() alcoholConsumptionOptions: any[] = [];
  @Input() smokingOptions: any[] = [];
  @Input() childrenOptions: string[] = ['Yes', 'No', 'Prefer not to say'];
  @Input() wakeupAtOptions: any[] = [];
  @Input() hobbiesOptions: any[] = [];
  @Input() fitnessOptions: any[] = [];
  @Input() personalityOptions: any[] = [];
  @Input() lifeGoalsOptions: any[] = [];
  @Input() maxionGoalsOptions: any[] = [];
  @Input() meetVibesOptions: any[] = [];
  @Input() subscriptionStatusOptions: string[] = [];

  // Output events
  @Output() toggleFilters = new EventEmitter<void>();
  @Output() toggleFilterSection = new EventEmitter<string>();
  @Output() updateSingleSelect = new EventEmitter<{
    key: string;
    event: any;
  }>();
  @Output() updateMultiSelect = new EventEmitter<{
    key: string;
    value: string;
    event: any;
  }>();
  @Output() applyAgeRangeFilter = new EventEmitter<{
    min: string;
    max: string;
  }>();
  @Output() applyStepRangeFilter = new EventEmitter<{
    type: string;
    min: string;
    max: string;
  }>();
  @Output() updateNumberInput = new EventEmitter<{ key: string; event: any }>();
  @Output() clearFilterChip = new EventEmitter<FilterChip>();
  @Output() clearAllFilters = new EventEmitter<void>();
  @Output() applyFilters = new EventEmitter<void>();

  onToggleFilters(): void {
    this.toggleFilters.emit();
  }

  onToggleFilterSection(section: string): void {
    this.toggleFilterSection.emit(section);
  }

  onUpdateSingleSelect(key: string, event: any): void {
    this.updateSingleSelect.emit({ key, event });
  }

  onUpdateMultiSelect(key: string, value: string, event: any): void {
    this.updateMultiSelect.emit({ key, value, event });
  }

  isSelected(key: string, value: string): boolean {
    const filterArray = this.selectedFilters[
      key as keyof FilterState
    ] as string[];
    return Array.isArray(filterArray) && filterArray.includes(value);
  }

  onApplyAgeRangeFilter(min: string, max: string): void {
    this.applyAgeRangeFilter.emit({ min, max });
  }

  onApplyStepRangeFilter(type: string, min: string, max: string): void {
    this.applyStepRangeFilter.emit({ type, min, max });
  }

  onUpdateNumberInput(key: string, event: any): void {
    this.updateNumberInput.emit({ key, event });
  }

  onClearFilterChip(chip: FilterChip): void {
    this.clearFilterChip.emit(chip);
  }

  onClearAllFilters(): void {
    this.clearAllFilters.emit();
  }

  onApplyFilters(): void {
    this.applyFilters.emit();
  }

  hasValidValue(chip: FilterChip): boolean {
    if (Array.isArray(chip.value)) {
      return chip.value.length > 0;
    }
    return !!chip.value && chip.value.trim() !== '';
  }

  get activeFilterChipsWithValues(): FilterChip[] {
    return this.activeFilterChips.filter((chip) => this.hasValidValue(chip));
  }

  // Dropdown methods
  toggleDropdown(field: string): void {
    this.openDropdowns[field] = !this.openDropdowns[field];
  }

  getSelectedText(
    field: string,
    options: any[],
    nameProperty?: string
  ): string {
    const selectedValues = (this.selectedFilters as any)[field] as any[];
    if (!selectedValues || selectedValues.length === 0) {
      return 'Select options';
    }

    if (selectedValues.length === 1) {
      const selectedValue = selectedValues[0];
      // Handle both object format (with id/name) and string format
      if (typeof selectedValue === 'object' && selectedValue.name) {
        return selectedValue.name;
      } else if (nameProperty) {
        const option = options.find((item: any) => item.id == selectedValue);
        return option ? option[nameProperty] : selectedValue;
      }
      return selectedValue;
    }

    // Handle multiple selections
    const selectedNames = selectedValues.map((item: any) => {
      if (typeof item === 'object' && item.name) {
        return item.name;
      } else if (nameProperty) {
        const option = options.find((optionItem: any) => optionItem.id == item);
        return option ? option[nameProperty] : item;
      }
      return item;
    });
    return `${selectedNames.length} selected`;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.multiselect-dropdown')) {
      // Close all dropdowns when clicking outside
      Object.keys(this.openDropdowns).forEach((key) => {
        this.openDropdowns[key] = false;
      });
    }
  }
}
